/**
 * What the upload dialog checks before any request: the `knowledge-base`
 * function's request rules (supabase/functions/knowledge-base/validation.ts:
 * the name's length, an empty file, then the type) and `kb_reserve_upload`'s
 * (migrations 20261004120000, 20261004130000 and 20261005120000: size, the
 * HTML, XML and YAML size, the same bytes already in the knowledge base, files
 * in it, storage, the text in it and in the workspace, the month's text), in
 * that order and in their words — change them together, and with Angular's
 * features/knowledge-base/files.ts. A file the server would refuse for these
 * never leaves the dialog. A retry is checked as `kb_confirm_upload` checks
 * one (`retryRefusal`, `retryRefusals`).
 */

import type { KnowledgeBaseFile, KnowledgeBaseLimits, KnowledgeBaseUsage } from "@/services/knowledge-base.service";
import { contentTypeOf, extensionOf, UNSUPPORTED_FILE_TYPE } from "@/utils/kb-file-types";

/** A knowledge base's name and a file's, as a file system allows them. */
export const NAME_MAX_CHARS = 255;

/** Why a new knowledge base's name would be refused, or null: required, and at most 255 characters, once trimmed. */
export function knowledgeBaseNameError(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Enter a name for the knowledge base.";
  return trimmed.length > NAME_MAX_CHARS ? `Keep the name to ${NAME_MAX_CHARS} characters or fewer.` : null;
}

const GB = 1024 ** 3;
const MB = 1024 ** 2;

/** 1,234 — `kb_fmt_int`. */
export const formatCount = (n: number) => n.toLocaleString("en-US");

/** "5 MB", "1.5 GB", "12 KB" — `kb_fmt_bytes`, with nothing stored reading "0 KB". */
export function formatBytes(bytes: number): string {
  const oneDecimal = (value: number) => value.toFixed(1).replace(/\.0$/, "");
  if (bytes >= GB) return `${oneDecimal(bytes / GB)} GB`;
  if (bytes >= MB) return `${oneDecimal(bytes / MB)} MB`;
  return bytes === 0 ? "0 KB" : `${Math.max(Math.round(bytes / 1024), 1)} KB`;
}

/** Passages as pages of text: about 3 a page, two significant figures. Mirrors kb_fmt_pages. */
export function formatPages(passages: number): string {
  const v = (Math.max(passages, 0) * 3) / 10;
  if (v === 0) return "0 pages";
  if (v < 1) return "less than a page";
  const whole = Math.round(v);
  if (whole === 1) return "about 1 page";
  const step = 10 ** Math.max(String(whole).length - 2, 0);
  return `about ${(Math.round(v / step) * step).toLocaleString("en-US")} pages`;
}

/** How much of a limit is used, in whole percent: 0 for none, at least 1 for any, at most 100. */
export const percentUsed = (used: number, max: number) =>
  used === 0 ? 0 : Math.min(100, Math.max(1, Math.round((used / max) * 100)));

/** "1 file", "3 files"; "1 knowledge base", "25 knowledge bases". */
export const counted = (n: number, noun: string) => `${formatCount(n)} ${n === 1 ? noun : `${noun}s`}`;

export const fileCount = (n: number) => counted(n, "file");

/** What one file may be, under the upload dialog's picker. */
export function fileLimits(
  limits: Pick<KnowledgeBaseLimits, "max_file_bytes" | "max_file_chunks" | "max_markup_file_bytes">,
) {
  const line = `Up to ${formatBytes(limits.max_file_bytes)} and ${formatPages(limits.max_file_chunks)} of text a file.`;
  return limits.max_markup_file_bytes < limits.max_file_bytes
    ? `${line} HTML, XML and YAML up to ${formatBytes(limits.max_markup_file_bytes)}.`
    : line;
}

/** The files a knowledge base's file limit counts: not a failed one, nor a replacement before it has replaced
 * its file. */
export const filesTowardLimit = (files: readonly Pick<KnowledgeBaseFile, "status" | "replaces_id">[]) =>
  files.filter((file) => file.status !== "error" && !file.replaces_id).length;

/** The passages a knowledge base's limit counts: its files being processed or ready. */
export const chunksTowardLimit = (files: readonly Pick<KnowledgeBaseFile, "status" | "chunk_count">[]) =>
  files.reduce((sum, file) => sum + (file.status === "processing" || file.status === "ready" ? file.chunk_count : 0), 0);

/** The plan's file limit reached: what an upload, or a retry of a failed file, is refused with. */
const fileLimitReached = (files: number) =>
  `This knowledge base already has ${formatCount(files)} files, the most your plan allows. Delete a file first.`;

/** What a duplicate is looked for among. */
type Held = Pick<KnowledgeBaseFile, "status" | "content_hash" | "display_name">;

/**
 * The same bytes already in the knowledge base, as `kb_reserve_upload` refuses them; null when they are new. A
 * failed file is no duplicate: its error may ask for the same bytes under another name.
 */
export function duplicateOf(contentHash: string, files: readonly Held[]): string | null {
  const same = files.find((file) => file.status !== "error" && file.content_hash === contentHash);
  return same ? `This file is already in this knowledge base, as "${same.display_name}".` : null;
}

/** A file a retry is checked against. */
type Listed = Held & Pick<KnowledgeBaseFile, "id" | "replaces_id">;

/**
 * Why a failed file may not run again, or null, as `kb_confirm_upload` refuses a retry: its bytes are in another
 * file now (both would be searched), or there is no room among the files (a failed file does not count toward
 * them, and running it again would — unless it replaces a file, which holds the room for it). A file of the old
 * upload has no hash: the function reads one from it.
 */
export function retryRefusal(
  file: Pick<KnowledgeBaseFile, "id" | "content_hash" | "replaces_id">,
  files: readonly Listed[],
  maxFiles: number,
): string | null {
  const others = files.filter((other) => other.id !== file.id);
  const duplicate = file.content_hash === null ? null : duplicateOf(file.content_hash, others);
  if (duplicate) return duplicate;
  if (file.replaces_id) return null;
  const counted = filesTowardLimit(others);
  return counted >= maxFiles ? fileLimitReached(counted) : null;
}

/**
 * Each failed file's refusal for a retry of several, or null for one that runs again: taken in order, as the
 * function takes them, each one that runs is counted against the next.
 */
export function retryRefusals(failed: readonly Listed[], files: readonly Listed[], maxFiles: number): (string | null)[] {
  let now = [...files];
  return failed.map((file) => {
    const refused = retryRefusal(file, now, maxFiles);
    if (!refused) now = now.map((other) => (other.id === file.id ? { ...other, status: "processing" as const } : other));
    return refused;
  });
}

/** Files a Delete or a Retry of several takes at once, as the function allows. */
export const BULK_MAX = 25;

export const BULK_TOO_MANY = `Select up to ${BULK_MAX} files at a time.`;

/** What there is room for: the plan's limits, and what the workspace and this knowledge base already use. */
export interface UploadRoom {
  limits: Pick<
    KnowledgeBaseLimits,
    | "max_file_bytes"
    | "max_markup_file_bytes"
    | "max_file_chunks"
    | "max_files_per_kb"
    | "max_storage_bytes"
    | "max_chunks_per_kb"
    | "max_chunks_per_workspace"
    | "max_chunks_per_month"
  >;
  /** The extensions held to `max_markup_file_bytes`. */
  markupExtensions: readonly string[];
  /** Files in the knowledge base that count toward its limit; null for a replacement, which takes no room
   * among them. */
  files: number | null;
  /** Bytes the workspace's files use. */
  storageBytes: number;
  /** Passages in this knowledge base, in all the workspace's, and processed this month. */
  kbChunks: number;
  workspaceChunks: number;
  monthChunks: number;
}

/**
 * The room an upload has, from the plan's usage and the knowledge base's files. A replacement takes no room
 * among the files, and the passages of the file it replaces are not counted against it (`kb_reserve_upload`
 * with `p_replaces_id`); its bytes are, until the two swap.
 */
export function uploadRoom(
  usage: Pick<UploadRoom, "limits"> &
    Pick<KnowledgeBaseUsage, "markup_extensions" | "storage_bytes" | "chunks" | "month_chunks">,
  files: readonly Pick<KnowledgeBaseFile, "id" | "status" | "chunk_count" | "replaces_id">[],
  replacing?: Pick<KnowledgeBaseFile, "id" | "chunk_count">,
): UploadRoom {
  return {
    limits: usage.limits,
    markupExtensions: usage.markup_extensions,
    files: replacing ? null : filesTowardLimit(files),
    storageBytes: usage.storage_bytes,
    kbChunks: chunksTowardLimit(files.filter((file) => file.id !== replacing?.id)),
    workspaceChunks: usage.chunks - (replacing?.chunk_count ?? 0),
    monthChunks: usage.month_chunks,
  };
}

/** A file picked for upload, with the SHA-256 of its bytes; null for one not read (refused before its bytes matter). */
export interface PickedFile {
  name: string;
  size: number;
  hash: string | null;
}

/** What a file is checked against before its bytes are read. */
type SizeRules = Pick<UploadRoom, "limits" | "markupExtensions">;

/** Whether a picked file is worth reading for its hash: one refused for its name, type or size is not. */
export const needsHash = (file: { name: string; size: number }, rules: SizeRules) =>
  refusedBeforeBytes(file, rules) === null;

function refusedBeforeBytes(file: { name: string; size: number }, { limits, markupExtensions }: SizeRules) {
  const name = file.name.trim();
  if (name.length > NAME_MAX_CHARS) {
    return `This file's name is longer than ${NAME_MAX_CHARS} characters. Rename it and upload it again.`;
  }
  if (file.size === 0) return "This file is empty.";
  if (!contentTypeOf(name)) return UNSUPPORTED_FILE_TYPE;
  if (file.size > limits.max_file_bytes) {
    return `This file is ${formatBytes(file.size)}. Files can be up to ${formatBytes(limits.max_file_bytes)}.`;
  }
  if (file.size > limits.max_markup_file_bytes && markupExtensions.includes(extensionOf(name))) {
    return `This file is ${formatBytes(file.size)}. HTML, XML and YAML files can be up to ${formatBytes(limits.max_markup_file_bytes)} for now.`;
  }
  return null;
}

function refusal(file: PickedFile, room: UploadRoom, held: readonly Held[]): string | null {
  const { limits } = room;
  const early = refusedBeforeBytes(file, room);
  if (early) return early;
  const duplicate = file.hash === null ? null : duplicateOf(file.hash, held);
  if (duplicate) return duplicate;
  if (room.files !== null && room.files >= limits.max_files_per_kb) return fileLimitReached(room.files);
  if (room.storageBytes + file.size > limits.max_storage_bytes) {
    return `Your files use ${formatBytes(room.storageBytes)} of the ${formatBytes(limits.max_storage_bytes)} your plan includes, and this one needs ${formatBytes(file.size)} more. Delete a file first.`;
  }
  if (room.kbChunks >= limits.max_chunks_per_kb) {
    return `This knowledge base is full: it holds ${formatPages(room.kbChunks)} of text, the most your plan allows. Delete a file first.`;
  }
  const workspaceCap = limits.max_chunks_per_workspace;
  if (workspaceCap !== null && room.workspaceChunks >= workspaceCap) {
    return `Your knowledge bases hold ${formatPages(room.workspaceChunks)} of text, the most your plan allows. Delete a file first.`;
  }
  if (room.monthChunks >= limits.max_chunks_per_month) {
    return `You have processed ${formatPages(room.monthChunks)} of text this month, all your plan includes. More can be added from the 1st of next month.`;
  }
  return null;
}

/**
 * Each file's refusal, or null for one that fits. The files are taken in
 * order, as the server would take them one after another: each one accepted
 * uses up its share of the room for the next, and is a slot whose bytes the
 * next may not repeat. `held` is the knowledge base's files.
 */
export function checkFiles(files: readonly PickedFile[], room: UploadRoom, held: readonly Held[]): (string | null)[] {
  let { files: count, storageBytes } = room;
  const taken = [...held];
  return files.map((file) => {
    const refused = refusal(file, { ...room, files: count, storageBytes }, taken);
    if (!refused) {
      if (count !== null) count += 1;
      storageBytes += file.size;
      taken.push({ status: "uploading", content_hash: file.hash, display_name: file.name.trim() });
    }
    return refused;
  });
}
